import { createContext, useCallback, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  /** false once the exit animation has started */
  visible: boolean;
}

interface ToastContextValue {
  /** Show a toast. Returns immediately. */
  toast: (message: string, variant?: ToastVariant) => void;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

/**
 * App-wide toast provider. Wrap your `<App />` once and call
 * `useToast()` anywhere to show ephemeral notifications.
 *
 * ```tsx
 * // In App.tsx
 * <ToastProvider><RouterProvider … /></ToastProvider>
 *
 * // In any component
 * const { toast } = useToast();
 * toast('Saved successfully');
 * toast('Connection failed', 'error');
 * ```
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems(prev => prev.map(t => (t.id === id ? { ...t, visible: false } : t)));
    setTimeout(() => {
      setItems(prev => prev.filter(t => t.id !== id));
    }, 400);
  }, []);

  const toast = useCallback((message: string, variant: ToastVariant = 'success') => {
    const id = ++nextId;
    // The same message again replaces the visible copy instead of stacking a
    // second one: repeated "Sync Now" clicks on a failing source used to pile up
    // identical red toasts over the page.
    setItems(prev => [
      ...prev.filter(t => !(t.message === message && t.variant === variant)).slice(-4),
      { id, message, variant, visible: true },
    ]);

    const duration = variant === 'error' ? 6000 : 3600;
    // Start exit animation, then remove
    setTimeout(() => {
      setItems(prev => prev.map(t => (t.id === id ? { ...t, visible: false } : t)));
    }, duration);
    setTimeout(() => {
      setItems(prev => prev.filter(t => t.id !== id));
    }, duration + 400);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {items.length > 0 && createPortal(
        <div
          className="fixed bottom-5 right-5 z-skip flex flex-col-reverse gap-2 pointer-events-none"
          aria-live="polite"
          aria-atomic="true"
        >
          {items.map(item => (
            <div
              key={item.id}
              role={item.variant === 'error' ? 'alert' : 'status'}
              className={clsx(
                'pointer-events-auto flex items-center gap-2.5 rounded-xl border px-4 py-3 text-xs font-medium shadow-lg transition-all duration-300',
                item.visible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-2 opacity-0',
                item.variant === 'success' && 'border-success/30 bg-success text-on-fill',
                item.variant === 'error'   && 'border-danger/30 bg-danger text-on-fill',
                item.variant === 'info'    && 'border-border bg-surface-0 text-ink',
              )}
            >
              <span aria-hidden="true">
                {item.variant === 'success' ? '✓' : item.variant === 'error' ? '✕' : 'ℹ'}
              </span>
              <span className="flex-1">{item.message}</span>
              <button
                onClick={() => dismiss(item.id)}
                className="ml-1 shrink-0 rounded-md p-0.5 opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-white/50"
                aria-label="Dismiss notification"
              >
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/** Access the toast function from any component inside `<ToastProvider>`. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
